import { useState, useCallback, useEffect, useRef } from 'react';
import type { UserPlan } from '@/types/user';
import { supabase } from '@/integrations/supabase/client';
import { getUserById } from './users';
import { uploadMedia } from '@/services/storage';
import {
  fetchThreads,
  fetchOlderMessages,
  fetchMessageById,
  createConversation,
  insertMessage,
  markMessagesRead,
  updateConversation,
  addMember,
  removeMember,
  editMessage as editMessageRow,
  softDeleteMessage,
  MAX_MESSAGE_MEDIA_BYTES,
  ALLOWED_MESSAGE_MEDIA_MIME,
} from '@/services/messaging';
import type { Message, Thread, SharedPostAttachment, MessageStatus } from '@/types/message';
import { track, AnalyticsEvent } from '@/lib/analytics';

export type { Message, Thread, SharedPostAttachment, MessageStatus };

const FREE_DM_LIMIT = 3;

export function useMessageStore(currentUserId: string, userPlan: UserPlan = 'free') {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingState, setTypingState] = useState<Record<string, Record<string, number>>>({});
  const threadsRef = useRef<Thread[]>([]);
  threadsRef.current = threads;

  const isAuthed = !!currentUserId && currentUserId !== '__none__';

  const reload = useCallback(async () => {
    if (!isAuthed) {
      setThreads([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setThreads(await fetchThreads(currentUserId));
    } catch (e) {
      console.error('[messages] load failed', e);
      setError(e instanceof Error ? e.message : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [currentUserId, isAuthed]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // ---- Realtime -----------------------------------------------------------
  useEffect(() => {
    if (!isAuthed) return;
    const channel = supabase
      .channel(`messaging-${currentUserId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const row = payload.new as { id: string; conversation_id: string };
        const thread = threadsRef.current.find((t) => t.id === row.conversation_id);
        if (!thread) {
          void reload();
          return;
        }
        if (thread.messages.some((m) => m.id === row.id)) return;
        void fetchMessageById(row.id, thread.participants).then((msg) => {
          if (!msg) return;
          setThreads((prev) =>
            prev.map((t) =>
              t.id === row.conversation_id && !t.messages.some((m) => m.id === msg.id)
                ? { ...t, messages: [...t.messages, msg] }
                : t,
            ),
          );
        });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, (payload) => {
        const row = payload.new as { id: string; conversation_id: string; content: string; edited_at: string | null; deleted_at: string | null };
        setThreads((prev) =>
          prev.map((t) =>
            t.id === row.conversation_id
              ? {
                  ...t,
                  messages: t.messages.map((m) =>
                    m.id === row.id
                      ? { ...m, text: row.deleted_at ? '' : row.content, editedAt: row.edited_at ?? undefined, deletedAt: row.deleted_at ?? undefined }
                      : m,
                  ),
                }
              : t,
          ),
        );
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'message_reads' }, (payload) => {
        const row = payload.new as { message_id: string; user_id: string };
        setThreads((prev) =>
          prev.map((t) => ({
            ...t,
            messages: t.messages.map((m) => {
              if (m.id !== row.message_id || m.seenBy.includes(row.user_id)) return m;
              const seenBy = [...m.seenBy, row.user_id];
              const others = t.participants.filter((p) => p !== m.senderId);
              const allSeen = others.length > 0 && others.every((p) => seenBy.includes(p));
              return { ...m, seenBy, status: (allSeen ? 'seen' : 'delivered') as MessageStatus };
            }),
          })),
        );
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversation_members' }, () => {
        void reload();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations' }, (payload) => {
        const row = payload.new as { id: string; group_name: string | null; context: string | null };
        setThreads((prev) =>
          prev.map((t) =>
            t.id === row.id ? { ...t, groupName: row.group_name ?? undefined, context: row.context ?? undefined } : t,
          ),
        );
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [currentUserId, isAuthed, reload]);

  // ---- Conversations ------------------------------------------------------
  const getOrCreateThread = useCallback(
    async (otherUserId: string, context?: string): Promise<string | null> => {
      if (!isAuthed) return null;
      const existing = threadsRef.current.find(
        (t) => !t.isGroup && t.participants.includes(otherUserId) && t.participants.length === 2,
      );
      if (existing) {
        if (context && !existing.context) {
          await updateConversation(existing.id, { context });
          setThreads((prev) => prev.map((t) => (t.id === existing.id ? { ...t, context } : t)));
        }
        return existing.id;
      }
      if (userPlan === 'free' && threadsRef.current.length >= FREE_DM_LIMIT) return null;
      try {
        const id = await createConversation(currentUserId, [otherUserId], { context });
        setThreads((prev) => [
          { id, participants: [currentUserId, otherUserId], messages: [], context, createdBy: currentUserId, createdAt: new Date().toISOString() },
          ...prev,
        ]);
        return id;
      } catch (e) {
        console.error('[messages] create conversation failed', e);
        return null;
      }
    },
    [currentUserId, isAuthed, userPlan],
  );

  const getOrCreateGroupThread = useCallback(
    async (participantIds: string[]): Promise<string | null> => {
      if (!isAuthed) return null;
      const allIds = Array.from(new Set([currentUserId, ...participantIds])).sort();
      const existing = threadsRef.current.find(
        (t) =>
          t.isGroup &&
          t.participants.length === allIds.length &&
          [...t.participants].sort().every((p, i) => p === allIds[i]),
      );
      if (existing) return existing.id;
      if (userPlan === 'free' && threadsRef.current.length >= FREE_DM_LIMIT) return null;
      try {
        const id = await createConversation(currentUserId, allIds, { isGroup: true });
        setThreads((prev) => [
          { id, participants: allIds, messages: [], isGroup: true, createdBy: currentUserId, createdAt: new Date().toISOString() },
          ...prev,
        ]);
        return id;
      } catch (e) {
        console.error('[messages] create group failed', e);
        return null;
      }
    },
    [currentUserId, isAuthed, userPlan],
  );

  const getThread = useCallback((threadId: string) => threads.find((t) => t.id === threadId), [threads]);

  // ---- Messages -----------------------------------------------------------
  const sendMessage = useCallback(
    async (threadId: string, text: string, sharedPost?: SharedPostAttachment, media?: { blob: Blob; type: 'image' | 'video' }) => {
      const body = text.trim();
      if (!body && !sharedPost && !media) return;

      let mediaUrl: string | undefined;
      if (media) {
        if (media.blob.size > MAX_MESSAGE_MEDIA_BYTES) {
          setError('Attachment too large');
          return;
        }
        if (!ALLOWED_MESSAGE_MEDIA_MIME.includes(media.blob.type)) {
          setError('Unsupported attachment format');
          return;
        }
        mediaUrl = (await uploadMedia('message-media', currentUserId, media.blob)) ?? undefined;
      }

      // Optimistic insert
      const tempId = `temp-${Date.now()}`;
      const optimistic: Message = {
        id: tempId,
        threadId,
        senderId: currentUserId,
        text: body,
        createdAt: new Date().toISOString(),
        sharedPost,
        mediaUrl,
        mediaType: media?.type,
        status: 'sent',
        seenBy: [currentUserId],
      };
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, messages: [...t.messages, optimistic] } : t)));

      try {
        const id = await insertMessage({
          conversationId: threadId,
          senderId: currentUserId,
          content: body,
          sharedPost,
          mediaUrl,
          mediaType: media?.type,
        });
        await markMessagesRead([id], currentUserId);
        void track(AnalyticsEvent.MESSAGE_SENT, {
          has_media: Boolean(mediaUrl),
          has_shared_post: Boolean(sharedPost),
        });
        setThreads((prev) =>
          prev.map((t) =>
            t.id === threadId
              ? { ...t, messages: t.messages.map((m) => (m.id === tempId ? { ...m, id } : m)) }
              : t,
          ),
        );
      } catch (e) {
        console.error('[messages] send failed', e);
        setError('Message could not be sent');
        setThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, messages: t.messages.filter((m) => m.id !== tempId) } : t)),
        );
      }
    },
    [currentUserId],
  );

  const loadOlderMessages = useCallback(async (threadId: string) => {
    const thread = threadsRef.current.find((t) => t.id === threadId);
    if (!thread || !thread.messages.length) return;
    try {
      const older = await fetchOlderMessages(threadId, thread.messages[0].createdAt, thread.participants);
      if (!older.length) return;
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, messages: [...older, ...t.messages] } : t)));
    } catch (e) {
      console.error('[messages] pagination failed', e);
    }
  }, []);

  const markThreadAsSeen = useCallback(
    (threadId: string) => {
      const thread = threadsRef.current.find((t) => t.id === threadId);
      if (!thread) return;
      const unread = thread.messages
        .filter((m) => !m.isSystem && !m.id.startsWith('temp-') && m.senderId !== currentUserId && !m.seenBy.includes(currentUserId))
        .map((m) => m.id);
      if (!unread.length) return;
      setThreads((prev) =>
        prev.map((t) =>
          t.id === threadId
            ? { ...t, messages: t.messages.map((m) => (unread.includes(m.id) ? { ...m, seenBy: [...m.seenBy, currentUserId] } : m)) }
            : t,
        ),
      );
      void markMessagesRead(unread, currentUserId);
    },
    [currentUserId],
  );

  const editMessage = useCallback(async (threadId: string, messageId: string, content: string) => {
    await editMessageRow(messageId, content);
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId
          ? { ...t, messages: t.messages.map((m) => (m.id === messageId ? { ...m, text: content, editedAt: new Date().toISOString() } : m)) }
          : t,
      ),
    );
  }, []);

  const deleteMessage = useCallback(async (threadId: string, messageId: string) => {
    await softDeleteMessage(messageId);
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId
          ? { ...t, messages: t.messages.map((m) => (m.id === messageId ? { ...m, text: '', deletedAt: new Date().toISOString() } : m)) }
          : t,
      ),
    );
  }, []);

  // ---- Group management ---------------------------------------------------
  const insertSystemMessage = useCallback(async (threadId: string, text: string) => {
    try {
      await insertMessage({ conversationId: threadId, senderId: null, content: text, isSystem: true });
    } catch (e) {
      console.error('[messages] system message failed', e);
    }
  }, []);

  const renameGroup = useCallback(
    async (threadId: string, name: string) => {
      await updateConversation(threadId, { group_name: name });
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, groupName: name } : t)));
      const actor = getUserById(currentUserId);
      await insertSystemMessage(threadId, `${actor?.name?.split(' ')[0] ?? 'Someone'} renamed the group to "${name}"`);
    },
    [currentUserId, insertSystemMessage],
  );

  const addMemberToGroup = useCallback(
    async (threadId: string, userId: string) => {
      await addMember(threadId, userId);
      setThreads((prev) =>
        prev.map((t) =>
          t.id === threadId && !t.participants.includes(userId) ? { ...t, participants: [...t.participants, userId] } : t,
        ),
      );
      const actor = getUserById(currentUserId);
      const added = getUserById(userId);
      await insertSystemMessage(threadId, `${actor?.name?.split(' ')[0] ?? 'Someone'} added ${added?.name?.split(' ')[0] ?? 'a user'}`);
    },
    [currentUserId, insertSystemMessage],
  );

  const leaveGroup = useCallback(
    async (threadId: string) => {
      const actor = getUserById(currentUserId);
      await insertSystemMessage(threadId, `${actor?.name?.split(' ')[0] ?? 'Someone'} left the group`);
      await removeMember(threadId, currentUserId);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
    },
    [currentUserId, insertSystemMessage],
  );

  // ---- Typing (ephemeral, client-side only) -------------------------------
  const setTyping = useCallback(
    (threadId: string, isTyping: boolean) => {
      setTypingState((prev) => {
        const threadTyping = { ...(prev[threadId] || {}) };
        if (isTyping) threadTyping[currentUserId] = Date.now();
        else delete threadTyping[currentUserId];
        return { ...prev, [threadId]: threadTyping };
      });
    },
    [currentUserId],
  );

  const getTypingUsers = useCallback(
    (threadId: string): string[] => {
      const threadTyping = typingState[threadId] || {};
      const now = Date.now();
      return Object.entries(threadTyping)
        .filter(([uid, ts]) => uid !== currentUserId && now - ts < 3000)
        .map(([uid]) => uid);
    },
    [typingState, currentUserId],
  );

  return {
    threads,
    loading,
    error,
    reload,
    getOrCreateThread,
    getOrCreateGroupThread,
    getThread,
    sendMessage,
    loadOlderMessages,
    editMessage,
    deleteMessage,
    renameGroup,
    addMemberToGroup,
    leaveGroup,
    markThreadAsSeen,
    currentUserId,
    setTyping,
    getTypingUsers,
  };
}
